'use client';

import * as React from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { cn } from '@wwe-portal/ui/lib/lib';
import { cva } from 'class-variance-authority';

const slideRangeVariants = cva('', {
  variants: {
    variant: {
      default:
        'linear-gradient(90deg, #FC8154 0%, #FFA800 53.93%, #FFBB00 100%)',
      blue: '#00BAFF',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const slideThumbVariants = cva('', {
  variants: {
    variant: {
      default:
        'radial-gradient(#ffffff 0%, #d3d3d3 20%,  #FFBB00 50%, #FFBB00 100%)',
      blue: 'radial-gradient(#1F233F 0%, #1F233F 40%,  #00BAFF 40%, #00BAFF 100%)',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const slideRingVariants = cva('', {
  variants: {
    variant: {
      default:
        'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 ',
      blue: 'focus-visible:ring-0',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> & {
    variant?: 'default' | 'blue';
  }
>(({ className, variant = 'default', ...props }, ref) => (
  <SliderPrimitive.Root
    ref={ref}
    className={cn(
      'relative flex w-full touch-none select-none items-center [&>span]:transition-all [&>span]:duration-200 [&>span]:ease-linear',
      className
    )}
    {...props}
  >
    <SliderPrimitive.Track className="bg-text400 relative h-2 w-full grow overflow-hidden rounded-full">
      <SliderPrimitive.Range
        className="bg-blue700 absolute h-full transition-all duration-200 ease-linear"
        style={{
          background: slideRangeVariants({ variant }),
        }}
      />
    </SliderPrimitive.Track>
    <SliderPrimitive.Thumb
      data-label="slider-thumb"
      className={cn(
        slideRingVariants({ variant }),
        'border-blue700 ring-offset-background drop-shadow-card-2 bg-text400 block h-5 w-5 rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-0 disabled:pointer-events-none disabled:opacity-50'
      )}
      style={{
        background: slideThumbVariants({ variant }),
      }}
    />
  </SliderPrimitive.Root>
));
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
